import React, { createContext, useCallback, useState, useContext, useEffect, useRef } from 'react';
import { getUserProfile } from '../api/authApi';
import { getTotalCardsApi, getCardBySlugAndCodeApi } from '../api/cardApi';
import { AUTH_STORAGE_EVENT, clearAuthTokens, getAccessToken, setAuthTokens } from '../utils/authStorage';

const AuthContext = createContext();
const USER_CACHE_KEY = 'user';
const USER_CACHE_TIME_KEY = 'userProfileCachedAt';
const CARDS_CACHE_KEY = 'userCards';
const CARDS_CACHE_TIME_KEY = 'userCardsCachedAt';
const DEFAULT_PROFILE_TTL = 5 * 60 * 1000;   // 5 minutes
const DEFAULT_CARDS_TTL = 5 * 60 * 1000;    // 5 minutes

export const AuthProvider = ({ children }) => {
    const [isLoggedIn, setIsLoggedIn] = useState(() => !!getAccessToken());

    const [user, setUser] = useState(() => {
        try {
            const stored = localStorage.getItem(USER_CACHE_KEY);
            return stored ? JSON.parse(stored) : null;
        } catch {
            return null;
        }
    });

    // ── Cards cache ──────────────────────────────────────────────────────────
    const [cards, setCards] = useState(() => {
        try {
            const stored = localStorage.getItem(CARDS_CACHE_KEY);
            const cachedAt = Number(localStorage.getItem(CARDS_CACHE_TIME_KEY) || 0);
            const isFresh = cachedAt && Date.now() - cachedAt < DEFAULT_CARDS_TTL;
            return stored && isFresh ? JSON.parse(stored) : [];
        } catch {
            return [];
        }
    });

    const [cardsLoading, setCardsLoading] = useState(false);

    // Prevent duplicate in-flight fetches
    const cardsFetchingRef = useRef(false);

    // ── Helpers ──────────────────────────────────────────────────────────────
    const persistUser = useCallback((userData) => {
        localStorage.setItem(USER_CACHE_KEY, JSON.stringify(userData));
        localStorage.setItem(USER_CACHE_TIME_KEY, String(Date.now()));
        setUser(userData);
    }, []);

    const persistCards = useCallback((cardList) => {
        localStorage.setItem(CARDS_CACHE_KEY, JSON.stringify(cardList));
        localStorage.setItem(CARDS_CACHE_TIME_KEY, String(Date.now()));
        setCards(cardList);
    }, []);

    const clearCardsCache = useCallback(() => {
        localStorage.removeItem(CARDS_CACHE_KEY);
        localStorage.removeItem(CARDS_CACHE_TIME_KEY);
        setCards([]);
    }, []);

    // ── Fetch cards (with TTL caching) ───────────────────────────────────────
    const fetchCards = useCallback(async ({ force = false } = {}) => {
        if (cardsFetchingRef.current) return;

        const cachedAt = Number(localStorage.getItem(CARDS_CACHE_TIME_KEY) || 0);
        const isFresh = cachedAt && Date.now() - cachedAt < DEFAULT_CARDS_TTL;

        if (!force && isFresh) {
            try {
                const stored = localStorage.getItem(CARDS_CACHE_KEY);
                if (stored) {
                    const parsed = JSON.parse(stored);
                    setCards(parsed);
                    return parsed;
                }
            } catch { /* fall through to re-fetch */ }
        }

        cardsFetchingRef.current = true;
        setCardsLoading(true);
        try {
            // Step 1: get basic card list (status, uniqueCode, slug, packageType, …)
            const basicCards = await getTotalCardsApi();

            // Step 2: for every card that has a slug, fetch its full cardData in parallel
            const enriched = await Promise.all(
                basicCards.map(async (card) => {
                    if (!card.slug || !card.uniqueCode) return card;
                    try {
                        const fullData = await getCardBySlugAndCodeApi(card.slug, card.uniqueCode);
                        // Merge: basic card fields first, then full cardData on top
                        return { ...card, ...fullData };
                    } catch {
                        // If the detail fetch fails just keep the basic card
                        return card;
                    }
                })
            );

            persistCards(enriched);
            return enriched;
        } catch (error) {
            console.error('Failed to fetch cards:', error);
            return [];
        } finally {
            setCardsLoading(false);
            cardsFetchingRef.current = false;
        }
    }, [persistCards]);

    // Force-refresh helper (e.g. after create / delete)
    const invalidateCards = useCallback(() => fetchCards({ force: true }), [fetchCards]);

    // ── Auto-fetch cards when user logs in or page is refreshed ─────────────
    useEffect(() => {
        if (isLoggedIn) {
            fetchCards({ force: true }); // always refetch on mount/refresh
        }
    }, [isLoggedIn]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Sync auth state across tabs ──────────────────────────────────────────
    useEffect(() => {
        const syncAuthState = () => {
            const hasToken = !!getAccessToken();
            setIsLoggedIn(hasToken);

            if (!hasToken) {
                localStorage.removeItem(USER_CACHE_KEY);
                localStorage.removeItem(USER_CACHE_TIME_KEY);
                setUser(null);
                clearCardsCache();
            }
        };

        window.addEventListener(AUTH_STORAGE_EVENT, syncAuthState);
        return () => window.removeEventListener(AUTH_STORAGE_EVENT, syncAuthState);
    }, [clearCardsCache]);

    // ── Auth actions ─────────────────────────────────────────────────────────
    const login = (accessToken, userData, refreshToken) => {
        setAuthTokens({ accessToken, refreshToken });
        setIsLoggedIn(true);
        persistUser(userData);
        // fetchCards will be triggered by the isLoggedIn useEffect above
    };

    const updateUser = (userData) => {
        persistUser(userData);
    };

    const refreshUserProfile = useCallback(async ({ force = false, maxAgeMs = DEFAULT_PROFILE_TTL } = {}) => {
        const cachedAt = Number(localStorage.getItem(USER_CACHE_TIME_KEY) || 0);
        let parsedCachedUser = null;

        try {
            const cachedUser = localStorage.getItem(USER_CACHE_KEY);
            parsedCachedUser = cachedUser ? JSON.parse(cachedUser) : null;
        } catch {
            parsedCachedUser = null;
        }

        const hasFreshCache = parsedCachedUser && cachedAt && Date.now() - cachedAt < maxAgeMs;

        if (!force && hasFreshCache) {
            setUser(parsedCachedUser);
            return parsedCachedUser;
        }

        const profileData = await getUserProfile();
        persistUser(profileData);
        return profileData;
    }, [persistUser]);

    const logout = () => {
        clearAuthTokens();
        localStorage.removeItem(USER_CACHE_KEY);
        localStorage.removeItem(USER_CACHE_TIME_KEY);
        setIsLoggedIn(false);
        setUser(null);
        clearCardsCache();
    };

    return (
        <AuthContext.Provider value={{
            isLoggedIn,
            user,
            login,
            logout,
            updateUser,
            refreshUserProfile,
            // ── Cards ──
            cards,
            cardsLoading,
            fetchCards,
            invalidateCards,
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
